import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IdentityService } from '../../../../services/identity/identity.service';
import { ToasterService } from '../../../../services/toaster/toaster.service';

@Component({
  selector: 'app-register-modal',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './register-modal.html',
  styleUrl: './register-modal.scss',
})
export class RegisterModal {
  // --- Injected Services ---
  private identityService = inject(IdentityService);
  private toaster = inject(ToasterService);
  private fb = inject(FormBuilder);

  // --- State for Registration Form ---
  registerForm!: FormGroup;
  password = signal('');
  submitted = signal(false);

  // --- Computed State ---
  passwordStrength = computed(() => this.calculatePasswordStrength(this.password()));

  ngOnInit() {
    this.registerForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(15)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
      dob: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: [''],
    }, { validators: this.passwordMatchValidator });

    // Update signal for strength meter when register form password changes
    this.f['password'].valueChanges.subscribe(val => {
      this.password.set(val || '');
    });
  }

  // Getter for easy access to form controls in the template
  get f() { return this.registerForm.controls; }

  // --- Methods ---
  register() {
    this.submitted.set(true);
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }
    console.log('Registering account with:', this.registerForm.value);

    this.identityService.register(this.registerForm.value).subscribe({
      next: (response) => {
        this.toaster.success('Account created successfully!');
        this.registerForm.reset();
        this.submitted.set(false);

        const closeBtn = document.querySelector('#registerModal [data-bs-dismiss="modal"]') as HTMLElement;
        if (closeBtn) {
          closeBtn.click();
        }
      },
      error: (err) => {
        console.error('Registration failed:', err);
        let errorMsg = 'Failed to register account.';
        if (Array.isArray(err?.error)) {
          errorMsg = err.error.map((e: any) => e.description).filter(Boolean).join(' ') || errorMsg;
        } else if (err?.error?.description) {
          errorMsg = err.error.description;
        } else if (err?.error?.message) {
          errorMsg = err.error.message;
        } else if (typeof err?.error === 'string') {
          errorMsg = err.error;
        }
        this.toaster.danger(errorMsg);
      }
    });
  }

  // --- Password Strength Logic ---
  private calculatePasswordStrength(p: string) {
    let level = 0;
    let text = '';

    if (!p) {
      return { level, text };
    }

    const hasUppercase = /[A-Z]/.test(p);
    const hasLowercase = /[a-z]/.test(p);
    const hasNumbers = /[0-9]/.test(p);
    const hasSymbols = /[^A-Za-z0-9]/.test(p);
    const isLongEnough = p.length >= 8;

    let strengthPoints = 0;
    if (hasUppercase) strengthPoints++;
    if (hasLowercase) strengthPoints++;
    if (hasNumbers) strengthPoints++;
    if (hasSymbols) strengthPoints++;

    if (isLongEnough) {
      if (strengthPoints >= 4) {
        level = 4; text = 'Very Strong';
      } else if (strengthPoints >= 3) {
        level = 3; text = 'Strong';
      } else if (strengthPoints >= 2) {
        level = 2; text = 'Medium';
      } else {
        level = 1; text = 'Weak';
      }
    } else {
      level = 1; text = 'Weak';
    }

    return { level, text };
  }

  // --- Custom Validator ---
  private passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  // --- UI Helpers ---
  getStrengthClass(level: number): string {
    switch (level) {
      case 1: return 'text-danger';
      case 2: return 'text-warning';
      case 3: return 'text-info';
      case 4: return 'text-success';
      default: return 'text-secondary';
    }
  }

  getStrengthBarClass(barNumber: number, strengthLevel: number): string {
    if (barNumber > strengthLevel) {
      return '';
    }
    switch (strengthLevel) {
      case 1: return 'bg-danger';
      case 2: return 'bg-warning';
      case 3: return 'bg-info';
      case 4: return 'bg-success';
      default: return '';
    }
  }
}
